import request from '@/config/axios'

// 订单 VO
export interface OrderVO {
  id?: number // 编号
  no?: string // 单号（dd-yyyyMMdd + 4 位自增，由后端生成）
  customerId?: number // 客户名称（客户编号）
  customerName?: string // 客户名称
  productId?: number // 产品（产品编号）
  productName?: string // 产品
  service?: string // 服务
  projectName?: string // 项目名称
  port?: string // 端口
  contactName?: string // 甲方联系人
  contactMobile?: string // 联系手机
  contactWechat?: string // 微信
  contactTitle?: string // 职务
  contactGender?: number // 性别（字典 system_user_sex）
  contactQq?: string // QQ
  contactEmail?: string // 邮箱
  province?: string // 省
  city?: string // 市
  district?: string // 区
  address?: string // 地址
  industry?: number // 行业（字典 crm_customer_industry）
  remark?: string // 备注
  signTime?: string // 签约时间
  effectDate?: string // 生效日期
  firstPaymentTime?: string // 首款时间
  deliveryDate?: string // 交付日期
  salesUserId?: number // 销售 / 商务
  salesUserName?: string
  productManagerId?: number // 产品经理
  productManagerName?: string
  techDirectorId?: number // 技术总监
  techDirectorName?: string
  iosDevId?: number // iOS 开发
  iosDevName?: string
  webDevId?: number // web 前端
  webDevName?: string
  mpDevId?: number // 小程序开发
  mpDevName?: string
  projectManagerId?: number // 项目经理
  projectManagerName?: string
  deliveryManagerId?: number // 交付经理
  deliveryManagerName?: string
  designOwnerId?: number // 设计负责人
  designOwnerName?: string
  androidDevId?: number // 安卓开发
  androidDevName?: string
  backendDevId?: number // 后端开发
  backendDevName?: string
  testOwnerId?: number // 测试负责人
  testOwnerName?: string
  ownerUserId?: number // 负责人
  ownerUserName?: string
  assigneeUserId?: number // 被分配人
  assigneeUserName?: string
  assignerUserId?: number // 分配人
  assignerUserName?: string
  devTechRequire?: string // 开发技术要求
  selfOutsource?: string // 自研 / 外包
  outsourceRemark?: string // 外包备注
  devLanguage?: string // 开发语言
  relatedFiles?: string // 相关文件（附件 URL，多个逗号分隔）
  accountInfo?: string // 系统账号信息
  prototypeLink?: string // 原型链接
  thirdAccountSummary?: string // 第三方账号汇总
  deliveryMaterial?: string // 交付资料
  projectSchedule?: string // 项目进度表
  contractAttach?: string // 合同附件
  onlineDoc?: string // 在线文档
  lanhuLink?: string // 蓝湖链接
  deliveryList?: string // 交付清单
  softwareCopyright?: string // 软著
  progress?: number // 进度（字典 crm_order_progress）
  progressPercent?: number // 进度说明（%）
  progressDesc?: string // 进度说明
  expectTime?: string // 预计时间
  costCheck?: number // 成本核算
  status?: number // 状态（字典 crm_order_status）
  stage?: number // 阶段（字典 crm_order_stage）
  contractNo?: string // 合同编号（表单只读）
  changeRemark?: string // 项目变更备注
  devCycle?: string // 项目开发周期
  bugSummary?: string // 项目 BUG 汇总
  followUpWay?: number // 跟进方式（字典 crm_follow_up_type）
  followUpContent?: string // 跟进内容
  devSummary?: string // 开发汇总
  followUpTime?: string // 跟进时间
  starred?: boolean // 是否标星
  topped?: boolean // 是否置顶
  ccUserIds?: string // 抄送人（用户编号，多个逗号分隔）
  ccUserNames?: string // 抄送人名称
  creator?: string // 申请人（用户编号）
  creatorName?: string // 申请人名称
  createTime?: Date // 创建时间
}

// 订单 API
export const OrderApi = {
  /**
   * 查询订单分页
   *
   * 六种口径（同一个 /page 接口，靠参数区分）：
   * - 我的订单 → sceneType = 1；我下属的订单 → sceneType = 3
   * - 别人分配给我的 → assignedToMe = true
   * - 我分配给别人的订单 → assignedByMe = true
   * - 全部订单 / 订单情况 → 三者都不传（后端不过滤）
   */
  getOrderPage: async (params: any) => {
    return await request.get({ url: `/crm/order/page`, params })
  },

  // 查询订单详情
  getOrder: async (id: number) => {
    return await request.get({ url: `/crm/order/get?id=` + id })
  },

  // 查询订单精简列表（下拉用）
  // 可选传 customerId：只返回该客户下的订单（「烂尾单分析」表单的「项目」下拉按客户过滤）
  getOrderSimpleList: async (params?: { customerId?: number }) => {
    return await request.get({ url: `/crm/order/simple-list`, params })
  },

  // 新增订单
  createOrder: async (data: OrderVO) => {
    return await request.post({ url: `/crm/order/create`, data })
  },

  // 修改订单
  updateOrder: async (data: OrderVO) => {
    return await request.put({ url: `/crm/order/update`, data })
  },

  // 删除订单
  deleteOrder: async (id: number) => {
    return await request.delete({ url: `/crm/order/delete?id=` + id })
  },

  // 批量分配订单（选择分配人员 / 一键分配）
  assignOrders: async (ids: number[], assigneeUserId: number) => {
    return await request.put({
      url: `/crm/order/assign`,
      data: { ids, assigneeUserId }
    })
  },

  // 批量标星 / 取消标星
  updateOrderStarred: async (ids: number[], starred: boolean) => {
    return await request.put({
      url: `/crm/order/update-starred`,
      data: { ids, starred }
    })
  },

  // 批量置顶 / 取消置顶
  updateOrderTopped: async (ids: number[], topped: boolean) => {
    return await request.put({
      url: `/crm/order/update-topped`,
      data: { ids, topped }
    })
  },

  // 导出订单
  exportOrder: async (params: any) => {
    return await request.download({ url: `/crm/order/export`, params })
  }
}
